import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInMinutes, differenceInSeconds } from 'date-fns/esm';

const TICK_INTERVAL_MS = 15_000;
// Beyond this, "last location status" reads as stale/disconnected rather than a precise minute count.
export const LIVE_MAP_STALE_THRESHOLD_MINUTES = 5;

export interface UseElapsedTimeLabelResult {
  label: string;
  isStale: boolean;
}

// Formats "time since `timestamp`" (e.g. "2 min"), re-evaluated every TICK_INTERVAL_MS.
export const useElapsedTimeLabel = (timestamp: string | null | undefined): UseElapsedTimeLabelResult => {
  const { t } = useTranslation();
  const [, forceTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => forceTick(tick => tick + 1), TICK_INTERVAL_MS);
    return (): void => clearInterval(interval);
  }, []);

  if (!timestamp) {
    return { label: t('liveMap.lastLocationStatus.unknown') as string, isStale: true };
  }

  const seconds = differenceInSeconds(new Date(), new Date(timestamp));
  const minutes = differenceInMinutes(new Date(), new Date(timestamp));

  if (seconds < 60) {
    return { label: t('liveMap.lastLocationStatus.justNow') as string, isStale: false };
  }

  return {
    label: t('liveMap.lastLocationStatus.minutesAgo', { count: minutes }) as string,
    isStale: minutes >= LIVE_MAP_STALE_THRESHOLD_MINUTES,
  };
};
